import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';

const W = 340;
const H = 128;
const PAD_X = 8;
const PAD_Y = 10;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Lightweight SVG sparklines (no chart library). Hover shows x-label + value.
 * @param {{ xLabels?: string[], emptyMessage?: string }} [extra]
 */
export function TrendSparkline({
  title,
  subtitle,
  values,
  yMin,
  yMax,
  formatValue,
  strokeClass = 'stroke-emerald-500',
  areaFill = 'rgb(16 185 129 / 0.14)',
  pointStroke = '#10b981',
  ariaLabel,
  xLabels,
  emptyMessage,
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  const n = values.length;
  const resolvedLabels = useMemo(() => {
    if (xLabels && xLabels.length === n) return xLabels;
    if (n === 7) return DAY_LABELS;
    return Array.from({ length: n }, (_, i) => String(i + 1));
  }, [xLabels, n]);
  const points = useMemo(() => {
    if (n < 2) return [];
    const spanX = W - PAD_X * 2;
    const spanY = H - PAD_Y * 2;
    const range = yMax - yMin || 1;
    return values.map((v, i) => {
      const x = PAD_X + (i / (n - 1)) * spanX;
      const clamped = Math.min(yMax, Math.max(yMin, v));
      const y = PAD_Y + spanY * (1 - (clamped - yMin) / range);
      return { x, y, v: values[i] };
    });
  }, [values, yMin, yMax, n]);

  const pathD = useMemo(() => {
    if (points.length < 2) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  }, [points]);

  const areaD = useMemo(() => {
    if (points.length < 2) return '';
    const baseY = H - PAD_Y;
    const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    const last = points[points.length - 1];
    const first = points[0];
    return `${line} L ${last.x.toFixed(1)} ${baseY} L ${first.x.toFixed(1)} ${baseY} Z`;
  }, [points]);

  const onPointer = useCallback(
    (clientX) => {
      const el = svgRef.current;
      if (!el || points.length < 2) return;
      const rect = el.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * W;
      const spanX = W - PAD_X * 2;
      let idx = Math.round(((x - PAD_X) / spanX) * (n - 1));
      idx = Math.max(0, Math.min(n - 1, idx));
      setHoverIdx(idx);
    },
    [points.length, n]
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (hoverIdx == null) return;
    // eslint-disable-next-line no-console
    console.debug('[DashboardTrendCharts] hover', { title, x: resolvedLabels[hoverIdx], value: values[hoverIdx] });
  }, [hoverIdx, title, values, resolvedLabels]);

  const active = hoverIdx != null ? points[hoverIdx] : null;

  if (n < 2) {
    return (
      <div className="rounded-xl border border-gray-100 bg-surface-50/80 p-4">
        <div className="mb-2">
          <h4 className="mf-heading-sm text-gray-900">{title}</h4>
          {subtitle ? <p className="mf-label mt-0.5 normal-case tracking-normal text-gray-500">{subtitle}</p> : null}
        </div>
        <p className="mf-body text-gray-500">{emptyMessage ?? 'Not enough points to plot a trend.'}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-surface-50/80 p-4">
      <div className="mb-2">
        <h4 className="mf-heading-sm text-gray-900">{title}</h4>
        {subtitle ? <p className="mf-label mt-0.5 normal-case tracking-normal text-gray-500">{subtitle}</p> : null}
      </div>
      <svg
        ref={svgRef}
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto touch-none select-none cursor-crosshair"
        onMouseMove={(e) => onPointer(e.clientX)}
        onMouseLeave={() => setHoverIdx(null)}
        onTouchStart={(e) => {
          if (e.touches[0]) onPointer(e.touches[0].clientX);
        }}
        onTouchMove={(e) => {
          if (e.touches[0]) onPointer(e.touches[0].clientX);
        }}
        onTouchEnd={() => setHoverIdx(null)}
      >
        {areaD ? <path d={areaD} fill={areaFill} /> : null}
        {pathD ? (
          <path
            d={pathD}
            fill="none"
            className={`${strokeClass} stroke-[2.5]`}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={hoverIdx === i ? 5 : 3}
            fill="#fff"
            stroke={pointStroke}
            strokeWidth={hoverIdx === i ? 2.5 : 1.5}
          />
        ))}
        {active ? (
          <g>
            <line
              x1={active.x}
              y1={PAD_Y}
              x2={active.x}
              y2={H - PAD_Y}
              className="stroke-gray-300"
              strokeDasharray="4 3"
              strokeWidth="1"
            />
          </g>
        ) : null}
      </svg>
      <div
        className={`mt-2 flex justify-between gap-0.5 font-medium text-gray-500 ${
          n > 7 ? 'text-[9px]' : 'text-[11px]'
        }`}
      >
        {resolvedLabels.map((d, i) => (
          <span key={`${d}-${i}`} className="truncate max-w-[3.25rem] text-center">
            {d}
          </span>
        ))}
      </div>
      {active ? (
        <p className="mf-body mt-2 text-gray-700">
          <span className="font-semibold text-gray-900">{resolvedLabels[hoverIdx]}</span>
          {' — '}
          {formatValue(active.v)}
        </p>
      ) : (
        <p className="mf-body mt-2 text-gray-500">Hover or drag along the series (demo trend).</p>
      )}
    </div>
  );
}

/**
 * @param {{ careCompliancePct: number, starRatingDisplay: number, facilityKey: string }} props
 */
export default function DashboardTrendCharts({ careCompliancePct, starRatingDisplay, facilityKey = 'all' }) {
  const careSeries = useMemo(() => {
    const end = careCompliancePct;
    const seed = String(facilityKey).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return Array.from({ length: 7 }, (_, i) => {
      const start = Math.max(72, end - 10);
      const t = i / 6;
      const wobble = Math.sin((i + seed * 0.01) * 0.9) * 2.5;
      const v = start + (end - start) * t + wobble;
      return Math.min(100, Math.max(68, Math.round(v)));
    });
  }, [careCompliancePct, facilityKey]);

  const starSeries = useMemo(() => {
    const end = starRatingDisplay;
    const start = Math.max(1, Math.min(end, end - 0.55));
    return Array.from({ length: 7 }, (_, i) => {
      const t = i / 6;
      const v = start + (end - start) * t + Math.sin(i * 0.5) * 0.04;
      return Math.min(5, Math.max(1, Math.round(v * 10) / 10));
    });
  }, [starRatingDisplay]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    // eslint-disable-next-line no-console
    console.debug('[DashboardTrendCharts] series built', { facilityKey, careSeries, starSeries });
  }, [facilityKey, careSeries, starSeries]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-100">
      <TrendSparkline
        title="Care minutes compliance (7-day trend)"
        subtitle="Synthetic facility roll-up for dashboard demo"
        values={careSeries}
        yMin={60}
        yMax={100}
        formatValue={(v) => `${v}%`}
        strokeClass="stroke-emerald-500"
        areaFill="rgb(16 185 129 / 0.14)"
        pointStroke="#10b981"
        ariaLabel="Seven day care minutes compliance trend chart"
      />
      <TrendSparkline
        title="Star rating trajectory (7-day trend)"
        subtitle="Predicted experience score (demo)"
        values={starSeries}
        yMin={1}
        yMax={5}
        formatValue={(v) => `${v} / 5`}
        strokeClass="stroke-amber-500"
        areaFill="rgb(245 158 11 / 0.16)"
        pointStroke="#f59e0b"
        ariaLabel="Seven day star rating trend chart"
      />
    </div>
  );
}
